/**
 * OrdoPageHeader — the app's page-title header, following the
 * `CustomersPageHeader` / `MenusPageHeader` pattern used across the
 * other Ordo Internal apps: optional eyebrow, a gray icon chip, and a
 * `text-2xl tracking-tight` title, closed off by a bottom divider
 * line. Weight is `font-medium` — one step lighter than the other
 * apps' `font-semibold`, because Outfit (the ordo brand font) reads
 * heavier than their Inter at the same weight. Right side carries
 * optional `actions`. No subtitle — none of the other apps render one;
 * put explanatory copy in the page body instead.
 *
 * Pages should pass the same lucide icon their sidebar nav item uses
 * (see `ICONS` in `components/ordo-sidebar.tsx`) so the header chip and
 * the active nav row read as the same destination.
 */

import { cn } from '@ordo/ui/cn'
import type { LucideIcon } from 'lucide-react'
import { type ReactNode, useEffect } from 'react'

import { usePageTitle } from '@/stores/page-title'

/**
 * The gray icon chip's look. Exported so an `iconSlot` control (e.g. the
 * cafeteria day picker) keeps the exact chip while adding behaviour.
 */
export const PAGE_HEADER_ICON_CHIP_CLASS =
  'flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-zinc-100 text-zinc-500 dark:bg-zinc-800/70 dark:text-zinc-400'

export interface OrdoPageHeaderProps {
  eyebrow?: string | null
  title: ReactNode
  icon?: LucideIcon
  /**
   * Replaces the static icon chip with a control of the caller's — e.g. Serve
   * Now's calendar chip that opens a day picker (PROD-1404). Style it with
   * {@link PAGE_HEADER_ICON_CHIP_CLASS}. Hidden below `md` with the title, like
   * the chip it replaces.
   */
  iconSlot?: ReactNode
  /**
   * Content CENTRED in the header at `md` and up, via a 1fr/auto/1fr grid that
   * only engages when this prop is passed — every other page keeps the plain
   * flex row. Below `md` it falls back into normal flow, where it is the only
   * thing on the row: the icon+title block is hidden there and the cafeteria
   * surfaces (PROD-1302, the one caller today) mount no top bar to fall back on.
   */
  trailing?: ReactNode
  /** Content rendered on the left, immediately after the title (e.g. a week stepper). */
  titleAdornment?: ReactNode
  actions?: ReactNode
  className?: string
}

export function OrdoPageHeader({
  eyebrow = null,
  title,
  icon: Icon,
  iconSlot,
  titleAdornment,
  trailing,
  actions,
  className,
}: OrdoPageHeaderProps) {
  // Surface a string title to the consolidated mobile top bar. The header's
  // own title row is hidden on mobile (the top bar shows it there instead).
  const setTitle = usePageTitle((s) => s.setTitle)
  const titleText = typeof title === 'string' ? title : null
  useEffect(() => {
    if (titleText === null) {
      return
    }
    setTitle(titleText)
    return () => setTitle(null)
  }, [titleText, setTitle])

  return (
    <section
      className={cn(
        'mb-6 flex flex-col gap-3 border-b border-border pb-4',
        // `trailing` centres itself in the header, which needs equal side
        // tracks; without it the header keeps the plain flex row it always had.
        trailing
          ? 'md:grid md:grid-cols-[1fr_auto_1fr] md:items-center md:gap-4'
          : 'md:flex-row md:items-center md:justify-between md:gap-4',
        // The title row is hidden on mobile (it lives in the top bar). When the
        // header has nothing else to show there, hide the whole section so its
        // divider/padding don't leave a residual rule above the page content.
        !eyebrow && actions == null && titleAdornment == null && trailing == null && 'hidden md:flex',
        className,
      )}>
      <div className="min-w-0 space-y-2">
        {eyebrow ? <p className="text-xs font-medium text-muted-foreground">{eyebrow}</p> : null}
        <div className="flex min-w-0 items-center gap-3">
          {/* Icon + title hide on mobile — the consolidated top bar renders the
              title there. Adornments (e.g. a week stepper) stay visible. */}
          <div className="hidden min-w-0 items-center gap-3 md:flex">
            {iconSlot ??
              (Icon ? (
                <span className={PAGE_HEADER_ICON_CHIP_CLASS}>
                  <Icon className="h-5 w-5" strokeWidth={1.8} />
                </span>
              ) : null)}
            <div className="min-w-0">
              <h1 className="truncate text-2xl font-medium tracking-tight">{title}</h1>
            </div>
          </div>
          {titleAdornment ? <div className="shrink-0 sm:ml-1">{titleAdornment}</div> : null}
        </div>
      </div>
      {trailing ? <div className="flex min-w-0 md:justify-center">{trailing}</div> : null}
      {actions != null && (
        <div className="flex flex-wrap items-center gap-2 md:shrink-0 md:flex-nowrap md:justify-end">{actions}</div>
      )}
    </section>
  )
}
